<script lang="ts">
import { computed, defineAsyncComponent, h } from 'vue';
import { LazyCollectionFieldSelect } from '#layers/data-model';

const LazyFormFieldEjectControl = defineAsyncComponent(() => import('#layers/blocks/components/Form/Field/FormFieldEjectControl.vue'));

const __DsBlock = {
	type: 'container',
	category: 'form',
	tags: ['form-container', 'form-children'],
	icon: 'lucide:pencil',
	slots: ({ props, getParentProps }) => props.ejected || !getParentProps()?.collectionId
		? [
				{
					name: 'default',
					// TODO only support relational block if a field is a relational field
					supportedBlocks: (b) => b.type === 'input',
				},
			]
		: [],

	provide: {
		model: {
			description: 'Read or write the value of the form field.',
		},
	},

	onInserted({ setProps, getProps }) {
		const fieldId = getProps().fieldId;

		if (!fieldId) {
			setProps({
				label: 'Some Field',
			});
		}
	},

	options: ({ form, context }) => form([
		{
			name: 'name',
			hidden: () => !!(context.getParentProps()?.collectionId || context.getRootContext().collectionId),
		},
		{
			name: 'fieldId',
			hidden: () => !(context.getParentProps()?.collectionId || context.getRootContext().collectionId),
			render: (props: any) => h(LazyCollectionFieldSelect, {
				...props,
				includePrimitiveFields: true,
				includeRelationFields: true,
				collectionId: context.getParentProps()?.collectionId || context.getRootContext().collectionId,
			}),
		},
		{
			name: 'label',
		},
		{
			name: 'description',
		},
		{
			name: 'required',
		},
		{
			name: 'disabled',
		},
		{
			name: 'disabledNotice',
		},
		{
			name: 'ejected',
			hidden: () => !(context.getParentProps()?.collectionId || context.getRootContext().collectionId),
			render: LazyFormFieldEjectControl,
		},
	]),
}</script>

<script lang="ts" setup>
import type { GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId } from '#layers/data-model';
import { pick } from '#layers/base/utils/object';
import { defineBlock } from '#layers/block-engine';
import PrimitiveFormField from '#layers/blocks/components/Form/Field/PrimitiveFormField.vue';
import RelationFormField from '#layers/blocks/components/Form/Field/RelationFormField.vue';
import { provideBaseFormFieldContext } from '~/composables/form-block/use-base-form-field-state';
import { useProjectSchemaStore } from '~/stores/project-schema';

interface FormFieldBlockProps {
	/**
	 * Name of the field to match errors.
	 */
	name?: string;

	fieldId?: GlobalFieldId;

	label?: string;
	description?: string;
	required?: boolean;
	/**
	 * By default the form field automatically render the appropriate input based on the collection settings. You can eject the field to customize it.
	 */
	ejected?: boolean;

	/**
	 * Disables this form field (and nested controls)
	 */
	disabled?: boolean;

	/**
	 * Provide a disabled notice that is shown to the user
	 */
	disabledNotice?: string;
}

const props = defineProps<FormFieldBlockProps>();

;

const { findPrimitiveFieldById, findSingleRelationFieldById } = useProjectSchemaStore();
const primitiveField = computed(() => props.fieldId ? findPrimitiveFieldById(props.fieldId as GlobalPrimitiveFieldId) : undefined);
const relationField = computed(() => props.fieldId ? findSingleRelationFieldById(props.fieldId as GlobalSingleRelationFieldId) : undefined);
const fieldName = computed(() => props.name ?? primitiveField.value?.apiName ?? relationField.value?.apiName);

const forwarded = computed(() => ({
	...pick(props, ['name', 'label', 'description', 'required', 'ejected']),
	'data-field-id': props.fieldId,
	'data-field-name': fieldName.value,
}));

provideBaseFormFieldContext({
	disabled: computed(() => props.disabled),
	disabledNotice: computed(() => props.disabledNotice),
});
</script>

<template>
	<PrimitiveFormField v-if="primitiveField" v-bind="forwarded" :primitive-field>
		<slot />
	</PrimitiveFormField>
	<RelationFormField v-else-if="relationField" v-bind="forwarded" :relation-field>
		<slot />
	</RelationFormField>
	<PrimitiveFormField
		v-else
		v-bind="forwarded"
		ejected
	>
		<slot />
	</PrimitiveFormField>
</template>
